// src/api.js

export const API_BASE = (() => {
  const env = process.env.REACT_APP_API_BASE;

  if (env && env.startsWith("http")) return env.replace(/\/$/, "");

  if (
    typeof window !== "undefined" &&
    window.location?.origin?.includes("localhost:3000")
  ) {
    return "";
  }

  if (
    typeof window !== "undefined" &&
    window.location?.origin?.startsWith("http")
  ) {
    return window.location.origin;
  }

  return "https://fin.montebrasil.com.br";
})();

/* =========================
   auth storage helpers
========================= */

const AUTH_KEY = "finbot_auth";
export const AUTH_CHANGED_EVENT = "finbot_auth_changed";

export function authGet() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.accessToken || !obj?.refreshToken) return null;
    return obj;
  } catch {
    return null;
  }
}

export function authSet(authObj) {
  localStorage.setItem(AUTH_KEY, JSON.stringify(authObj));
  try {
    if (typeof window !== "undefined") window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
  } catch {}
}

export function authClear() {
  localStorage.removeItem(AUTH_KEY);
  try {
    if (typeof window !== "undefined") window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
  } catch {}
}

/* =========================
   API status (para banner global)
========================= */

let _apiStatus = {
  online: typeof navigator !== "undefined" ? navigator.onLine : true,
  lastErrorCode: null, // "offline" | "backend_down" | "timeout" | "http_error" | "unauthorized" | "parse_error"
  lastErrorAt: null,
};

const _statusSubs = new Set();

function setApiStatus(patch) {
  _apiStatus = { ..._apiStatus, ...patch };
  for (const fn of _statusSubs) {
    try {
      fn(_apiStatus);
    } catch {}
  }
}

export function getApiStatus() {
  return _apiStatus;
}

export function subscribeApiStatus(fn) {
  _statusSubs.add(fn);
  // fire imediata
  try {
    fn(_apiStatus);
  } catch {}
  return () => _statusSubs.delete(fn);
}

// mantém online/offline em sync
if (typeof window !== "undefined" && typeof navigator !== "undefined") {
  window.addEventListener("online", () => setApiStatus({ online: true }));
  window.addEventListener("offline", () => setApiStatus({ online: false }));
}

/* =========================
   Error typing
========================= */

export class ApiError extends Error {
  constructor({
    code,
    message,
    status = 0,
    url = "",
    path = "",
    method = "",
    details = null,
  }) {
    super(message || code || "api_error");
    this.name = "ApiError";
    this.code = code; // offline | backend_down | timeout | http_error | unauthorized | parse_error
    this.status = status;
    this.url = url;
    this.path = path;
    this.method = method;
    this.details = details;
  }
}

function humanMessageFor(code, status) {
  if (code === "offline") return "Sem internet. Conecte-se para atualizar os dados.";
  if (code === "timeout") return "Tempo excedido ao conectar. Tente novamente.";
  if (code === "backend_down")
    return "Servidor indisponível no momento. Tente novamente em instantes.";
  if (code === "unauthorized")
    return "Sessão expirada. Faça login novamente.";
  if (code === "parse_error")
    return "Resposta inválida do servidor. Tente novamente.";
  if (code === "http_error") {
    if (status >= 500) return "Erro temporário no servidor. Tente novamente.";
    return "Não foi possível completar a solicitação. Verifique e tente novamente.";
  }
  return "Falha de comunicação. Tente novamente.";
}

function isAuthPath(p) {
  return p.startsWith("/api/auth/");
}

/* =========================
   refresh flow
========================= */

let refreshingPromise = null;

async function refreshAccessToken() {
  // Lê o refresh token atual (pode mudar durante a vida do refresh)
  const cur = authGet();
  const refreshToken = cur?.refreshToken;

  if (!refreshToken) {
    throw new ApiError({
      code: "unauthorized",
      status: 401,
      message: humanMessageFor("unauthorized", 401),
      path: "/api/auth/refresh",
    });
  }

  if (!refreshingPromise) {
    refreshingPromise = (async () => {
      const out = await apiFetchRaw("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
        timeoutMs: 12000,
      });

      if (!out?.accessToken) {
        throw new ApiError({
          code: "unauthorized",
          status: 401,
          message: humanMessageFor("unauthorized", 401),
          path: "/api/auth/refresh",
          details: { reason: "refresh_failed" },
        });
      }

      // ✅ Re-lê o estado mais recente antes de persistir
      // Evita: sobrescrever auth após logout/troca de usuário.
      const latest = authGet();
      if (!latest?.refreshToken || latest.refreshToken !== refreshToken) {
        throw new ApiError({
          code: "unauthorized",
          status: 401,
          message: humanMessageFor("unauthorized", 401),
          path: "/api/auth/refresh",
          details: { reason: "auth_changed_during_refresh" },
        });
      }

      const next = { ...(latest || {}), accessToken: out.accessToken };
      authSet(next);
      return next.accessToken;
    })().finally(() => {
      refreshingPromise = null;
    });
  }

  return refreshingPromise;
}

/* =========================
   body normalization helper (SAFE)
========================= */

function isPlainObject(x) {
  return (
    x != null &&
    typeof x === "object" &&
    (x.constructor === Object || Object.getPrototypeOf(x) === Object.prototype)
  );
}

function normalizeOpts(opts = {}) {
  const next = { ...opts };
  const headers = { ...(opts.headers || {}) };

  if (isPlainObject(next.body)) {
    const hasCT = Object.keys(headers).some(
      (k) => k.toLowerCase() === "content-type"
    );
    if (!hasCT) headers["Content-Type"] = "application/json";
    next.body = JSON.stringify(next.body);
  }

  next.headers = headers;
  return next;
}

/* =========================
   fetch with timeout + classify network errors
========================= */

function buildUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  const url = API_BASE ? `${API_BASE}${p}` : p;
  return { p, url };
}

function shouldTreatAsBackendDown(status) {
  return status === 0 || status === 502 || status === 503 || status === 504;
}

async function fetchWithTimeout(url, opts = {}, timeoutMs = 12000) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, { ...opts, signal: controller.signal });
    return res;
  } catch (e) {
    // rethrow para classificar acima
    throw e;
  } finally {
    clearTimeout(t);
  }
}

function classifyFetchError(err) {
  // AbortController timeout
  if (err?.name === "AbortError") return "timeout";
  // Falhas típicas do fetch em mobile/web: "Failed to fetch", "NetworkError", etc.
  return "backend_down";
}

/* =========================
   raw fetch (no auto json/error)
   - agora com timeout + erros tipados
========================= */

async function apiFetchRaw(path, opts = {}) {
  const { p, url } = buildUrl(path);
  const o = normalizeOpts(opts);
  const timeoutMs = Number.isFinite(o.timeoutMs) ? o.timeoutMs : 12000;
  delete o.timeoutMs;

  // Offline guard (evita spinner infinito)
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    const e = new ApiError({
      code: "offline",
      status: 0,
      url,
      path: p,
      method: (o.method || "GET").toUpperCase(),
      message: humanMessageFor("offline", 0),
    });
    setApiStatus({
      online: false,
      lastErrorCode: e.code,
      lastErrorAt: Date.now(),
    });
    throw e;
  }

  let res;
  try {
    res = await fetchWithTimeout(
      url,
      {
        ...o,
        headers: {
          Accept: "application/json",
          ...(o.headers || {}),
        },
      },
      timeoutMs
    );
  } catch (err) {
    const code = classifyFetchError(err);
    const e = new ApiError({
      code,
      status: 0,
      url,
      path: p,
      method: (o.method || "GET").toUpperCase(),
      message: humanMessageFor(code, 0),
      details: { original: String(err?.message || err) },
    });
    setApiStatus({ lastErrorCode: e.code, lastErrorAt: Date.now() });
    throw e;
  }

  if (!res.ok) {
    const status = res.status;

    // backend fora / gateway down
    const code = shouldTreatAsBackendDown(status)
      ? "backend_down"
      : status === 401
      ? "unauthorized"
      : "http_error";

    const text = await res.text().catch(() => "");
    const e = new ApiError({
      code,
      status,
      url,
      path: p,
      method: (o.method || "GET").toUpperCase(),
      message: humanMessageFor(code, status),
      details: { bodyText: text },
    });
    setApiStatus({ lastErrorCode: e.code, lastErrorAt: Date.now() });
    throw e;
  }

  // sucesso => limpa erro global
  setApiStatus({ lastErrorCode: null });

  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) return null;

  try {
    return await res.json();
  } catch (err) {
    const e = new ApiError({
      code: "parse_error",
      status: res.status,
      url,
      path: p,
      method: (o.method || "GET").toUpperCase(),
      message: humanMessageFor("parse_error", res.status),
      details: { original: String(err?.message || err) },
    });
    setApiStatus({ lastErrorCode: e.code, lastErrorAt: Date.now() });
    throw e;
  }
}

/* =========================
   main apiFetch (auth + refresh)
   - agora usando apiFetchRaw como base
========================= */

export async function apiFetch(path, opts = {}) {
  const { p, url } = buildUrl(path);
  const o = normalizeOpts(opts);
  const timeoutMs = Number.isFinite(o.timeoutMs) ? o.timeoutMs : 12000;

  const isAuthRoute = isAuthPath(p);

  const cur = authGet();
  const accessToken = !isAuthRoute ? cur?.accessToken : null;

  async function doFetch(withToken) {
    // usa fetchWithTimeout para manter timeout no fluxo auth também
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      const e = new ApiError({
        code: "offline",
        status: 0,
        url,
        path: p,
        method: (o.method || "GET").toUpperCase(),
        message: humanMessageFor("offline", 0),
      });
      setApiStatus({
        online: false,
        lastErrorCode: e.code,
        lastErrorAt: Date.now(),
      });
      throw e;
    }

    let res;
    try {
      res = await fetchWithTimeout(
        url,
        {
          ...o,
          headers: {
            Accept: "application/json",
            ...(withToken ? { Authorization: `Bearer ${withToken}` } : {}),
            ...(o.headers || {}),
          },
        },
        timeoutMs
      );
    } catch (err) {
      const code = classifyFetchError(err);
      const e = new ApiError({
        code,
        status: 0,
        url,
        path: p,
        method: (o.method || "GET").toUpperCase(),
        message: humanMessageFor(code, 0),
        details: { original: String(err?.message || err) },
      });
      setApiStatus({ lastErrorCode: e.code, lastErrorAt: Date.now() });
      throw e;
    }

    return res;
  }

  // 1) tenta com token atual
  let res = await doFetch(accessToken || null);

  // 2) 401 => refresh 1 vez e retry (exceto /api/auth/*)
  if (!isAuthRoute && res.status === 401) {
    const hasRefresh = !!authGet()?.refreshToken;
    if (hasRefresh) {
      try {
        const newAccess = await refreshAccessToken();
        res = await doFetch(newAccess);
      } catch (err) {
        authClear();
        // se refresh falhou, padroniza como unauthorized
        if (err instanceof ApiError) throw err;
        throw new ApiError({
          code: "unauthorized",
          status: 401,
          url,
          path: p,
          method: (o.method || "GET").toUpperCase(),
          message: humanMessageFor("unauthorized", 401),
        });
      }
    }
  }

  if (!res.ok) {
    const status = res.status;

    const code = shouldTreatAsBackendDown(status)
      ? "backend_down"
      : status === 401
      ? "unauthorized"
      : "http_error";

    const text = await res.text().catch(() => "");
    const e = new ApiError({
      code,
      status,
      url,
      path: p,
      method: (o.method || "GET").toUpperCase(),
      message: humanMessageFor(code, status),
      details: { bodyText: text },
    });
    setApiStatus({ lastErrorCode: e.code, lastErrorAt: Date.now() });
    throw e;
  }

  setApiStatus({ lastErrorCode: null });

  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) return null;

  try {
    return await res.json();
  } catch (err) {
    const e = new ApiError({
      code: "parse_error",
      status: res.status,
      url,
      path: p,
      method: (o.method || "GET").toUpperCase(),
      message: humanMessageFor("parse_error", res.status),
      details: { original: String(err?.message || err) },
    });
    setApiStatus({ lastErrorCode: e.code, lastErrorAt: Date.now() });
    throw e;
  }
}
